import { useAdminSubmitSuretyAccount } from "@portal/api/account/admin";
import { Modal } from "@portal/components/layout/Modal";
import { SectionNavLayout } from "@portal/components/multiform/SectionNavLayout";
import type { StepConfig } from "@portal/components/multiform/types";
import { Card } from "@portal/components/ui/Card";
import { useIsMinimalBondUIEnabled } from "@portal/hooks/useIsMinimalBondUIEnabled";
import { useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import type { BondFormData } from "../../../../types";
import { useClearContractHistoryFields } from "../../../agent/account/create/steps/history/useClearContractHistoryFields";
import type { AccountSubmissionAnalytics } from "../../../agent/account/create/useAccountSubmissionAnalytics";
import {
	BondRequestCreateContext,
	type PrincipalCompanyOption,
} from "../../../agent/request/create/BondRequestCreateView";
import { transformBondFormForSubmission } from "../../../agent/request/create/schema";
import { FullSubmitModalHeader } from "../../shared/submission/FullSubmitModalHeader";
import {
	type AdminCombinedFormData,
	type CompanyFormData,
	toAdminAccountCreateRequest,
} from "./schema";
import { accountTermsStepConfig } from "./steps/account-terms/AccountTerms.config";
import { additionalInfoStepConfig } from "./steps/additional-info/AdditionalInfo.config";
import { bondStepConfig } from "./steps/bond/Bond.config";
import { bondAdditionalInfoStepConfig } from "./steps/bond-additional-info/BondAdditionalInfo.config";
import { bondTermsStepConfig } from "./steps/bond-terms/BondTerms.config";
import { detailsStepConfig } from "./steps/details/Details.config";
import { financialsStepConfig } from "./steps/financials/Financials.config";
import { historyStepConfig } from "./steps/history/History.config";
import { principalStepConfig } from "./steps/principal/Principal.config";

const defaultBondFormData: BondFormData = {
	templateId: "",
	upload: { type: "", files: [] },
};

const prequalSteps: StepConfig<AdminCombinedFormData>[] = [
	principalStepConfig,
	detailsStepConfig,
	historyStepConfig,
	financialsStepConfig,
	additionalInfoStepConfig,
	accountTermsStepConfig,
];

const combinedSteps: StepConfig<AdminCombinedFormData>[] = [
	principalStepConfig,
	detailsStepConfig,
	historyStepConfig,
	financialsStepConfig,
	additionalInfoStepConfig,
	accountTermsStepConfig,
	bondStepConfig,
	bondAdditionalInfoStepConfig,
	bondTermsStepConfig,
];

type AdminAccountCreateModalProps = {
	isCombined?: boolean;
	onClose: () => void;
	formMethods: ReturnType<typeof useForm<AdminCombinedFormData>>;
	/**
	 * Owned by AdminAccountCreateController. This modal is only ever reached by
	 * escalating a quick submit, so it continues that flow rather than starting
	 * one of its own.
	 */
	analytics: AccountSubmissionAnalytics<AdminCombinedFormData>;
};

export const AdminAccountCreateModal = ({
	isCombined,
	onClose,
	formMethods,
	analytics,
}: AdminAccountCreateModalProps) => {
	const [, setSearchParams] = useSearchParams();
	useClearContractHistoryFields(formMethods);
	const setSearchParamsRef = useRef(setSearchParams);
	setSearchParamsRef.current = setSearchParams;

	useEffect(() => {
		return () => {
			setSearchParamsRef.current(
				(prev) => {
					const next = new URLSearchParams(prev);
					next.delete("section");
					next.delete("st");
					return next;
				},
				{ replace: true },
			);
		};
	}, []);

	if (isCombined) {
		return (
			<AdminCombinedAccountCreateView
				onClose={onClose}
				formMethods={formMethods}
				analytics={analytics}
			/>
		);
	}
	return (
		<AdminPrequalAccountCreateView
			onClose={onClose}
			formMethods={formMethods}
			analytics={analytics}
		/>
	);
};

const AdminPrequalAccountCreateView = ({
	onClose,
	formMethods,
	analytics,
}: {
	onClose: () => void;
	formMethods: ReturnType<typeof useForm<AdminCombinedFormData>>;
	analytics: AccountSubmissionAnalytics<AdminCombinedFormData>;
}) => {
	const { submitAccount, isSubmitting } = useAdminSubmitSuretyAccount();

	const handleSuccess = async (data: AdminCombinedFormData) => {
		submitAccount({
			data: toAdminAccountCreateRequest(data),
			onSuccess: (account) => {
				analytics.onAccountCreated(account.id, data, data.account.modelSize);
				onClose();
			},
			onError: analytics.onSubmissionFailed,
		});
	};
	const handleClose = () => {
		analytics.abandonCurrentFlow();
		onClose();
	};

	return (
		<Modal onClickOutside={handleClose} itemClassName="py-[10px] px-[9px] w-[100vw] h-[100vh]">
			<Card className="flex flex-col w-full h-full max-h-full">
				<div className="w-full px-5 py-4 border-b border-gray-200 flex-shrink-0 rounded-t-md">
					<FullSubmitModalHeader
						title="New Account Submission"
						onClose={handleClose}
						closeTrackingId="account-admin-prequal-full-close"
					/>
				</div>
				<div className="w-full flex-1 overflow-y-auto rounded-b-md">
					<FormProvider {...formMethods}>
						<SectionNavLayout
							formMethods={formMethods}
							steps={prequalSteps}
							confirmationMessage="Are you sure you want to submit?"
							isSubmitting={isSubmitting}
							scrollable={false}
							onSuccess={handleSuccess}
							trackingPrefix="account-admin-prequal-full"
							{...analytics.sectionNavCallbacks}
						/>
					</FormProvider>
				</div>
			</Card>
		</Modal>
	);
};

const AdminCombinedAccountCreateView = ({
	onClose,
	formMethods,
	analytics,
}: {
	onClose: () => void;
	formMethods: ReturnType<typeof useForm<AdminCombinedFormData>>;
	analytics: AccountSubmissionAnalytics<AdminCombinedFormData>;
}) => {
	const { submitAccount, isSubmitting } = useAdminSubmitSuretyAccount();
	const [bondFormData, setBondFormData] = useState<BondFormData>(defaultBondFormData);
	const bondFormRef = useRef(bondFormData);
	bondFormRef.current = bondFormData;

	const isMinimalUIEnabled = useIsMinimalBondUIEnabled();
	const { control, getValues, resetField } = formMethods;
	const steps = useMemo(
		() =>
			isMinimalUIEnabled
				? combinedSteps.filter((step) => step !== bondAdditionalInfoStepConfig)
				: combinedSteps,
		[isMinimalUIEnabled],
	);

	const accountTermsStatus = useWatch({ control, name: "accountTerms.status" });

	useEffect(() => {
		if (accountTermsStatus !== "approved" && getValues("bondTerms.status")) {
			resetField("bondTerms");
		}
	}, [accountTermsStatus]);

	const accountNaicsCodes = useWatch({
		control,
		name: "account.naicsCodes",
	});

	const formCompanies = useWatch({
		control,
		name: "account.details.companies",
	}) as CompanyFormData[] | undefined;

	const companies = useMemo((): PrincipalCompanyOption[] => {
		if (!formCompanies) return [];
		return formCompanies
			.filter((c) => c?.name)
			.map((c) => ({
				name: c.name ?? "",
				isPrimary: c.isPrimaryCompany === true,
			}));
	}, [formCompanies]);

	useEffect(() => {
		const bondForm = transformBondFormForSubmission(bondFormData);
		formMethods.setValue("quote.bondForm", bondForm ?? null);
	}, [bondFormData, formMethods]);

	const handleSuccess = async (data: AdminCombinedFormData) => {
		submitAccount({
			data: toAdminAccountCreateRequest(data),
			onSuccess: (account) => {
				analytics.onAccountCreated(account.id, data, data.account.modelSize);
				onClose();
			},
			onError: analytics.onSubmissionFailed,
		});
	};
	const handleClose = () => {
		analytics.abandonCurrentFlow();
		onClose();
	};

	return (
		<BondRequestCreateContext.Provider
			value={{ accountNaicsCodes, bondFormData, setBondFormData, companies }}
		>
			<Modal onClickOutside={handleClose} itemClassName="py-[10px] px-[9px] w-[100vw] h-[100vh]">
				<Card className="flex flex-col w-full h-full max-h-full">
					<div className="w-full px-5 py-4 border-b border-gray-200 flex-shrink-0 rounded-t-md">
						<FullSubmitModalHeader
							title="New Account + Bond Request Submission"
							onClose={handleClose}
							closeTrackingId="account-admin-combined-full-close"
						/>
					</div>
					<div className="w-full flex-1 overflow-y-auto rounded-b-md">
						<FormProvider {...formMethods}>
							<SectionNavLayout
								formMethods={formMethods}
								steps={steps}
								confirmationMessage="Are you sure you want to submit?"
								isSubmitting={isSubmitting}
								scrollable={false}
								onSuccess={handleSuccess}
								trackingPrefix="account-admin-combined-full"
								{...analytics.sectionNavCallbacks}
							/>
						</FormProvider>
					</div>
				</Card>
			</Modal>
		</BondRequestCreateContext.Provider>
	);
};
